import { useMutation, useQuery } from 'react-query'
import {
  EditPlanParamType,
  EditPlanTableParamType,
  updateVolumeMutateParam,
} from './PlannedVolume.types'
import { request } from 'Features/auth/axios-client'
import { WORKCENTRE_STATUS_FETCH_TIMEOUT } from 'Common/Constants/Constants'

const fetchWeeklyVolume = async ({
  sourcesiteId,
  planId,
  workcentreId,
}: EditPlanParamType) => {
  const data = await request({
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/edit_plan_volume_weekly_data`,
  })
  return data
}

export const useWeeklyVolume = (params: EditPlanParamType, enabled = false) => {
  return useQuery(
    ['weekly-volume', params.planId, params.sourcesiteId, params.workcentreId],
    () => fetchWeeklyVolume(params),
    {
      refetchOnMount: false,
      select: (data) => data?.data?.data,
      cacheTime: 0,
      enabled: enabled,
      refetchOnWindowFocus: false,
    }
  )
}

const fetchWeeklyVolumeTable = async ({
  sourcesiteId,
  planId,
  workcentreId,
  selectedDate,
}: EditPlanTableParamType) => {
  const data = await request({
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/edit_plan_volume_weekly_table_data?date=${selectedDate}`,
  })
  return data
}

export const useWeeklyVolumeTable = (
  params: EditPlanTableParamType,
  enabled = false
) => {
  return useQuery(
    [
      'capacity-table',
      params.planId,
      params.selectedDate,
      params.workcentreId,
      params.sourcesiteId,
    ],
    () => fetchWeeklyVolumeTable(params),
    {
      refetchOnMount: false,
      select: (data) => data?.data?.data,
      enabled: params.selectedDate && enabled ? true : false,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  )
}

const updateVolume = async (updateParams: updateVolumeMutateParam) => {
  const { dataToServer, savePlan } = updateParams
  const { planId, sourcesiteId, workcentreId } = dataToServer[0]
  const { data } = await request({
    method: 'put',
    data: { volumeData: [...dataToServer] },
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/edit_plan_volume_weekly_table_data`,
  })
  return { data, savePlan }
}

export const useUpdateVolume = (
  onSuccess: (savePlan: 'false' | 'true') => void,
  onError: () => void
) => {
  //const queryClient = useQueryClient()
  return useMutation(updateVolume, {
    onSuccess: async (data) => {
      // await queryClient.invalidateQueries([
      //   'weekly-volume',
      //   variablesPassed.planId,
      //   variablesPassed.sourcesiteId,
      //   variablesPassed.workcentreId,
      // ])
      const { savePlan } = data
      onSuccess(savePlan)
    },
    onError: () => {
      onError()
    },
  })
}

const runUpdateVolume = async (
  updateParams: EditPlanParamType & { savePlan: 'false' | 'true' }
) => {
  const { planId, sourcesiteId, workcentreId, savePlan } = updateParams
  const { data } = await request({
    method: 'post',
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/run_edit_plan_volume`,
    data: {
      savePlan,
    },
  })

  return data
}

export const useRunUpdateVolume = (
  onSuccess: () => void,
  onError: (error: unknown) => void
) => {
  return useMutation(runUpdateVolume, {
    onSuccess: () => {
      onSuccess()
    },
    onError: (error) => {
      onError(error)
    },
  })
}

const fetchWorkcentreRunStatus = async (params: EditPlanParamType) => {
  const { planId, sourcesiteId, workcentreId } = params
  const data = await request({
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/run-status`,
  })
  return data
}

export const useWorkcentreRunStatus = (params: EditPlanParamType) => {
  return useQuery(
    [
      'workcentre-run-status',
      params.planId,
      params.sourcesiteId,
      params.workcentreId,
    ],
    () => fetchWorkcentreRunStatus(params),
    {
      cacheTime: 0,
      select: (response) => response?.data?.data,
      refetchOnWindowFocus: true,
      refetchInterval: WORKCENTRE_STATUS_FETCH_TIMEOUT,
    }
  )
}

export const useSavePlan = (
  onSavePlanSuccess: (message: string, savePlan: 'true' | 'false') => void,
  onSavePlanFail: (message: string) => void
) => {
  return useMutation(updateVolume, {
    onSuccess: async (data) => {
      const { savePlan } = data
      onSavePlanSuccess('Save plan success', savePlan)
    },
    onError: () => onSavePlanFail('Save plan failed.'),
  })
}
